import { createQueryKeys } from '@lukemorales/query-key-factory';
import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { StampHistoryApi } from 'api';
import type { StampHistory } from 'api/methods/StampHistoryApi';

/**
 * [StampHistory] Query key 생성
 * --
 */
export const stampHistoryQueryKeys = createQueryKeys('stamp_history', {
  getStampHistory: (
    idx: number,
    params?: { [key: string]: string | number }
  ) => [idx, params],
  getStampHistories: (params?: { [key: string]: string | number }) => [params],
});

/**
 * [StampHistory] 스탬프 적립 내역 전체 조회
 * --
 */
export const useGetStampHistorys = (
  params?: {
    [key: string]: string | number;
  },
  options?: UseQueryOptions<any, any>
) => {
  const data = useQuery({
    queryKey: stampHistoryQueryKeys.getStampHistories(params).queryKey,
    queryFn: () => StampHistoryApi.getStampHistories(params),
    ...options,
  });
  return data;
};

/**
 * [StampHistory] 스탬프 적립 내역 상세 조회
 * --
 */
export const useGetStampHistory = (
  idx: number,
  params?: { [key: string]: string | number }
) => {
  const data = useQuery({
    queryKey: stampHistoryQueryKeys.getStampHistory(idx, params).queryKey,
    queryFn: () => StampHistoryApi.getStampHistory(idx, params),
  });

  return data;
};

/**
 * [StampHistory] 스탬프 적립 내역 생성
 * --
 */
export const useCreateStampHistory = (body: StampHistory) => {
  const { mutateAsync: createStampHistory, ...result } = useMutation({
    mutationFn: (variables: StampHistory) =>
      StampHistoryApi.createStampHistory(variables),
    onSuccess: (data, variables) => {
      console.log(data, variables);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return { createStampHistory, ...result };
};

/**
 * [StampHistory] 스탬프 적립 내역 수정
 * --
 */
export const useUpdateStampHistory = (body: StampHistory) => {
  const { mutateAsync: updateStampHistory, ...result } = useMutation({
    mutationFn: (variables: StampHistory) =>
      StampHistoryApi.updateStampHistory(variables),
    onSuccess: (data, variables) => {
      console.log(data, variables);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return { updateStampHistory, ...result };
};

/**
 * [StampHistory] 스탬프 적립 내역 삭제
 * --
 */
export const useDeleteStampHistory = (idx: number) => {
  const { mutateAsync: deleteStampHistory, ...result } = useMutation({
    mutationFn: (variables: number) =>
      StampHistoryApi.deleteStampHistory(variables),
    onSuccess: (data, variables) => {
      console.log(data, variables);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return { deleteStampHistory, ...result };
};

/**
 * [StampHistory] 스탬프 적립
 * --
 */
export const useRewardStampHistory = () => {
  const { mutateAsync: rewardStampHistory, ...result } = useMutation({
    mutationFn: (variables: StampHistory) =>
      StampHistoryApi.rewardStampHistory(variables),
  });

  return { rewardStampHistory, ...result };
};

/**
 * [StampHistory] 스탬프 벌크 수정
 * --
 */
export const useBulkUpdateStampHistory = () => {
  const { mutateAsync: bulkUpdateStampHistory, ...result } = useMutation({
    mutationFn: (variables: StampHistory[]) =>
      StampHistoryApi.bulkUpdateStampHistory(variables),
  });

  return { bulkUpdateStampHistory, ...result };
};
